import React, { useCallback, useEffect } from 'react';
import { CloseIcon, ExternalLinkIcon } from '../icons';

// One image in a lightbox set — a generated result or a reference.
export type LightboxImage = {
  // Stable identity, so a caller can open the set at a specific image.
  key: string;
  src: string;
  name: string;
  // What this image is in the set ("Result", "Reference 2 of 3") — the caption's
  // badge, and the only thing distinguishing otherwise identical thumbnails.
  role: string;
  // Paint the transparency checkerboard behind it (cut-out / alpha results).
  alpha?: boolean;
  // Set when the image is an Asset Lab node, which makes it reachable in the
  // catalog — the caption then offers "Open in Asset Lab".
  assetId?: number;
};

// Fullscreen image preview used by the studio wherever an image is clicked (the
// references panel, the History cards). It shows the *images* — not a record
// with metadata around them — and steps between the ones in the set with the
// arrows or ← / →.
//
// It closes on Escape, on the ✕, and on a click anywhere outside the image
// itself: only the image and the caption bar swallow the click, so the whole
// backdrop — including the empty space beside a portrait image — is a target.
export const MagicImageLightbox: React.FC<{
  images: LightboxImage[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  // Absent when the host has nowhere to navigate to; the caption's Asset Lab
  // action is then hidden even for images that carry an assetId.
  onOpenAsset?: (assetId: number) => void;
}> = ({ images, index, onIndexChange, onClose, onOpenAsset }) => {
  const count = images.length;
  // Guard against an index left behind by a shrinking set (a deleted item).
  const current = images[Math.min(index, count - 1)];

  const step = useCallback(
    (delta: number) => {
      if (count > 1) onIndexChange((index + delta + count) % count);
    },
    [count, index, onIndexChange],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') step(-1);
      if (e.key === 'ArrowRight') step(1);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, step]);

  if (!current) return null;

  // Clicks on the image / caption / arrows must not reach the backdrop handler.
  const keep = (e: React.MouseEvent) => e.stopPropagation();

  // Kept as a local so it stays narrowed inside the button's handler.
  const revealAssetId = onOpenAsset ? current.assetId : undefined;

  return (
    <div className="al-magic-lightbox" role="dialog" aria-label={current.name} onClick={onClose}>
      <button
        type="button"
        className="al-magic-lightbox__close"
        title="Close (Esc)"
        onClick={onClose}
      >
        <CloseIcon size={18} />
      </button>

      {count > 1 && (
        <>
          <button
            type="button"
            className="al-magic-lightbox__nav al-magic-lightbox__nav_prev"
            title="Previous (←)"
            aria-label="Previous image"
            onClick={(e) => {
              keep(e);
              step(-1);
            }}
          >
            <Chevron dir="left" />
          </button>
          <button
            type="button"
            className="al-magic-lightbox__nav al-magic-lightbox__nav_next"
            title="Next (→)"
            aria-label="Next image"
            onClick={(e) => {
              keep(e);
              step(1);
            }}
          >
            <Chevron dir="right" />
          </button>
        </>
      )}

      <div
        className={`al-magic-lightbox__frame${current.alpha ? ' al-magic-result_alpha' : ''}`}
        onClick={keep}
      >
        <img className="al-magic-lightbox__img" src={current.src} alt={current.name} />
      </div>

      <div className="al-magic-lightbox__bar" onClick={keep}>
        <span className="al-magic-lightbox__role">{current.role}</span>
        <span className="al-magic-lightbox__name">{current.name}</span>
        {count > 1 && (
          <span className="al-magic-lightbox__counter">
            {Math.min(index, count - 1) + 1} / {count}
          </span>
        )}
        {revealAssetId != null && (
          <button
            type="button"
            className="al-magic-lightbox__action"
            title="Show this asset in its Asset Lab category"
            onClick={() => onOpenAsset?.(revealAssetId)}
          >
            <ExternalLinkIcon size={12} />
            Open in Asset Lab
          </button>
        )}
      </div>
    </div>
  );
};

const Chevron: React.FC<{ dir: 'left' | 'right' }> = ({ dir }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d={dir === 'left' ? 'M10 3L5 8l5 5' : 'M6 3l5 5-5 5'}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
